import { useEffect } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { getPostBySlug, BLOG_POSTS } from "../lib/data/blog";
import { setSEO } from "../lib/data/seo";
import { Reveal, RevealLines } from "../components/motion/Reveal";
import SpotlightImage from "../components/site/SpotlightImage";
import ContactBlock from "../components/site/ContactBlock";

export default function BlogPost() {
  const { slug } = useParams();
  const post = getPostBySlug(slug);

  useEffect(() => {
    if (!post) return;
    setSEO({
      title: `${post.title} — Maze Security Solutions Journal`,
      description: post.excerpt,
      keywords: post.keywords,
      canonical: `${window.location.origin}/blog/${post.slug}`,
      jsonLd: {
        "@context": "https://schema.org",
        "@type": "Article",
        headline: post.title,
        description: post.excerpt,
        datePublished: post.date,
        author: { "@type": "Organization", name: post.author },
        publisher: { "@type": "Organization", name: "Maze Security Solutions" },
        articleSection: post.category,
      },
    });
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [post]);

  if (!post) return <Navigate to="/blog" replace />;

  const others = BLOG_POSTS.filter((p) => p.slug !== post.slug).slice(0, 3);

  return (
    <div className="pt-24">
      <article>
        <section className="pt-12 md:pt-20 pb-8 bg-[#F4F4F0]" data-testid="post-hero">
          <div className="mx-auto max-w-[1000px] px-6 lg:px-10">
            <Link to="/blog" className="font-mono text-[10px] tracking-[0.28em] uppercase text-[#D92323]">
              ← Journal
            </Link>
            <div className="mt-6 flex items-center gap-4 font-mono text-[10px] tracking-[0.22em] uppercase text-black/60">
              <span className="text-[#D92323]">{post.category}</span>
              <span>·</span>
              <span>{post.date}</span>
              <span>·</span>
              <span>{post.readTime}</span>
            </div>
            <RevealLines
              lines={[post.title]}
              className="mt-6 font-display text-4xl md:text-6xl lg:text-7xl tracking-tight leading-[1.02] text-[#060913]"
            />
            <p className="mt-8 text-lg md:text-2xl text-[#5C5F66] leading-relaxed max-w-3xl">
              {post.excerpt}
            </p>
          </div>
        </section>

        <section className="bg-[#F4F4F0] pb-8">
          <div className="mx-auto max-w-[1200px] px-6 lg:px-10">
            <SpotlightImage
              src={post.image}
              alt={post.title}
              caption={post.category}
              className="aspect-[16/9]"
            />
          </div>
        </section>

        <section className="bg-[#F4F4F0] py-16 md:py-24" data-testid="post-body">
          <div className="mx-auto max-w-[820px] px-6 lg:px-10 space-y-12">
            {post.body.map((section, i) => (
              <Reveal key={i} delay={i * 0.05}>
                <div>
                  <div className="font-mono text-[10px] tracking-[0.28em] uppercase text-[#D92323]">
                    {String(i + 1).padStart(2, "0")}
                  </div>
                  <h2 className="mt-3 font-display text-3xl md:text-4xl tracking-tight leading-tight text-[#060913]">
                    {section.h}
                  </h2>
                  <p className="mt-4 text-lg md:text-xl leading-relaxed text-[#060913]/85">
                    {section.p}
                  </p>
                </div>
              </Reveal>
            ))}
          </div>
        </section>

        <section className="bg-white border-y border-black/10 py-20">
          <div className="mx-auto max-w-[1200px] px-6 lg:px-10">
            <div className="font-mono text-[10px] tracking-[0.28em] uppercase text-[#D92323] mb-6">
              Continue Reading
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {others.map((p) => (
                <Link
                  key={p.slug}
                  to={`/blog/${p.slug}`}
                  className="group border border-black/10 hover:border-[#D92323] p-6 transition-colors bg-[#F4F4F0]"
                >
                  <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-[#D92323]">
                    {p.category}
                  </div>
                  <h3 className="mt-3 font-display text-2xl tracking-tight leading-tight text-[#060913] group-hover:text-[#D92323]">
                    {p.title}
                  </h3>
                  <p className="mt-3 text-sm text-[#5C5F66] leading-relaxed">
                    {p.excerpt.slice(0, 120)}…
                  </p>
                  <div className="mt-4 font-mono text-[10px] tracking-[0.22em] uppercase text-black/50">
                    {p.readTime} · {p.date}
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        <section className="bg-[#060913] text-white py-20">
          <div className="mx-auto max-w-[1200px] px-6 lg:px-10 grid grid-cols-1 md:grid-cols-12 gap-8 items-center">
            <div className="md:col-span-6">
              <div className="font-mono text-[10px] tracking-[0.28em] uppercase text-[#D92323]">
                Talk to the operators
              </div>
              <h2 className="mt-4 font-display text-3xl md:text-5xl tracking-tight leading-[0.95]">
                Questions about your specific site? Call us.
              </h2>
            </div>
            <div className="md:col-span-6">
              <ContactBlock variant="dark" testid={`post-${post.slug}-contact`} />
            </div>
          </div>
        </section>
      </article>
    </div>
  );
}
